/**
 * O rascunho de um trecho guardado no servidor a cada pausa na digitação (E171.F3, ADR-0122 §2.6) e o
 * estado dele na tela. Saiu do editor antigo na E202.F2 (GUS-145, ADR-0145 §6): o editor no
 * documento da mesa usa o mesmo mecanismo.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { ptBR } from '@/i18n/pt-BR';
import {
  PAUSA_DO_RASCUNHO_MS,
  type ConteudoDoEditor,
  type SituacaoDoRascunho,
} from '@/lib/editorManual';

const t = ptBR.editorManual;

export type EstadoDaGravacao = 'parado' | 'guardando' | 'guardado' | 'falhou';

function iguais(a: ConteudoDoEditor, b: ConteudoDoEditor): boolean {
  return a.acao === b.acao && a.texto === b.texto && a.fundamento === b.fundamento;
}

/**
 * Guarda o rascunho a cada pausa depois de uma mudança; o primeiro conteúdo não se guarda. Ao fechar
 * com uma pausa pendente, guarda na hora; `cancelar` (descartar ou salvar a versão) não guarda mais.
 * `guardarJa` (E202.F2.C04, "Guardar o rascunho e sair") guarda na hora o que ainda não chegou ao
 * servidor, inclusive depois de uma pausa que falhou, e devolve a recusa a quem chamou. Ela lê o
 * conteúdo por `lerAtual` e só termina com a versão mais recente confirmada (E205.F2.C02,
 * F-E205.F2-01): o que a pessoa digita durante a espera vai numa guarda seguinte, e a recusa da última
 * é a recusa de quem chamou. Enquanto ela corre, a pausa não dispara outra guarda do mesmo texto.
 *
 * Uma guarda por vez, na ordem (E205.F2.C03, F-E205.F2-01): a pausa, a saída do editor e `guardarJa`
 * só partem depois de a guarda anterior responder, então a resposta de uma guarda anterior nunca chega
 * depois da seguinte, nem na tela nem no servidor. `guardarJa` espera a guarda em voo antes de
 * conferir o que falta levar.
 */
export function useRascunho(
  conteudo: ConteudoDoEditor,
  guardar: (c: ConteudoDoEditor) => Promise<SituacaoDoRascunho>,
  situacaoInicial: SituacaoDoRascunho | null,
) {
  const [estado, setEstado] = useState<EstadoDaGravacao>(
    situacaoInicial === null ? 'parado' : 'guardado',
  );
  const [situacao, setSituacao] = useState<SituacaoDoRascunho | null>(situacaoInicial);
  const primeiro = useRef(true);
  const pendente = useRef<ConteudoDoEditor | null>(null);
  // O último conteúdo que o servidor tem (o primeiro, que não se guarda, conta como guardado).
  const ultimo = useRef(conteudo);
  const guardarAtual = useRef(guardar);
  guardarAtual.current = guardar;
  const pausa = useRef<number | null>(null);
  const pararPausa = useCallback((): void => {
    if (pausa.current !== null) window.clearTimeout(pausa.current);
    pausa.current = null;
  }, []);
  // `guardarJa` em curso: ela mesma leva o que mudar; a pausa não dispara outra guarda.
  const emGuardaJa = useRef(false);
  // A guarda anterior: a próxima só sai depois de ela responder (confirmada ou recusada).
  const anterior = useRef<Promise<unknown>>(Promise.resolve());
  const levar = useCallback((c: ConteudoDoEditor): Promise<SituacaoDoRascunho> => {
    const vez = () => guardarAtual.current(c);
    const guarda = anterior.current.then(vez, vez);
    anterior.current = guarda;
    return guarda;
  }, []);

  useEffect(() => {
    if (primeiro.current) {
      primeiro.current = false;
      return undefined;
    }
    pendente.current = conteudo;
    if (emGuardaJa.current) return undefined;
    pausa.current = window.setTimeout(() => {
      pausa.current = null;
      pendente.current = null;
      setEstado('guardando');
      levar(conteudo)
        .then((s) => {
          ultimo.current = conteudo;
          setSituacao(s);
          setEstado('guardado');
        })
        .catch(() => setEstado('falhou'));
    }, PAUSA_DO_RASCUNHO_MS);
    return pararPausa;
  }, [conteudo, pararPausa, levar]);

  useEffect(
    () => () => {
      if (pendente.current !== null) {
        levar(pendente.current).catch(() => undefined);
      }
    },
    [levar],
  );

  const guardarJa = useCallback(
    async (lerAtual: () => ConteudoDoEditor): Promise<void> => {
      emGuardaJa.current = true;
      pararPausa();
      try {
        // A guarda em voo (a pausa que já disparou) responde primeiro: ela pode ter levado este texto.
        await anterior.current.then(
          () => undefined,
          () => undefined,
        );
        let atual = lerAtual();
        while (!iguais(ultimo.current, atual)) {
          pendente.current = null;
          setEstado('guardando');
          try {
            const s = await levar(atual);
            ultimo.current = atual;
            setSituacao(s);
            setEstado('guardado');
          } catch (err) {
            setEstado('falhou');
            throw err;
          }
          // O que mudou durante a espera ainda não está no servidor: mais uma guarda.
          atual = lerAtual();
        }
        pendente.current = null;
      } finally {
        emGuardaJa.current = false;
      }
    },
    [pararPausa, levar],
  );

  return {
    estado,
    situacao,
    guardarJa,
    cancelar: () => {
      pendente.current = null;
    },
  };
}

export function EstadoDoRascunho({
  estado,
  situacao,
}: {
  estado: EstadoDaGravacao;
  situacao: SituacaoDoRascunho | null;
}) {
  const emConflito = estado === 'guardado' && situacao !== null && situacao !== 'vigente';
  return (
    <div role="status" aria-live="polite" data-testid="estado-do-rascunho" data-estado={estado}>
      {emConflito ? (
        <div className="border border-warning-500 bg-warning-50 p-sc-2 text-body-sm text-warning-700">
          <p className="font-semibold" data-testid="situacao-do-rascunho" data-situacao={situacao}>
            {t.situacao[situacao]}
          </p>
          <p>{t.situacaoAjuda[situacao]}</p>
        </div>
      ) : (
        <p
          className={
            estado === 'falhou' ? 'text-body-sm text-danger-700' : 'text-caption text-fg-muted'
          }
        >
          {t.gravacao[estado]}
        </p>
      )}
    </div>
  );
}
